/**
 * Task 161B — minimal main-flow block for the `influencers` family.
 *
 * The previous Task 161 demoted the typed `InfluencerSection` (count /
 * category / details) into the advanced area and put a generic
 * `DeliverablesEditor` in the main flow. The User-PO judged BOTH as wrong
 * UX: count / category look like a real influencer-campaign workflow that
 * doesn't exist yet, and a generic deliverables-rows editor is the wrong
 * shape for "we'll run influencer activations for you, scope TBD".
 *
 * This section is the corrected primary UX. It surfaces:
 *  - a workflow-later banner so the agency manager understands the
 *    detailed influencer workflow is planned but NOT implemented today,
 *  - a single scope-text textarea bound to `config.details` so the
 *    manager can write the package's actual scope in prose.
 *
 * `count` / `category` are intentionally not editable here. Any values
 * persisted from older packages round-trip untouched through
 * `normalizeConfig('influencer', ...)` (it copies `count` / `category` /
 * `details` straight through), so this UI change is non-destructive.
 *
 * Pricing for influencer lines is reused from the existing per-line
 * commercial columns in `AdvancedTermsSection` (pricing_model /
 * retainer_amount / retainer_currency / spend_percentage / cadence) — we
 * do NOT invent influencer-specific pricing here.
 */

import { normalizeConfig, serializeConfig } from './packageServiceConfigModel';
import { SimpleField } from './formPrimitives';

export function InfluencerScopeSection({
  config,
  // Accepted for API symmetry with the other per-family main-flow sections
  // (`ServiceConfigSection`, `DeliverablesEditor`). The underlying
  // `SimpleField` does not yet honor a `disabled` prop, and the drawer's
  // save button is already gated on `submitting`, so this is a no-op
  // today — intentionally prefixed to satisfy `no-unused-vars`.
  disabled: _disabled,
  onChange,
}: {
  readonly config: Record<string, unknown>;
  readonly disabled?: boolean;
  readonly onChange: (nextRaw: Record<string, unknown>) => void;
}) {
  // Normalize once so the textarea reads the stored value cleanly, and any
  // legacy `count` / `category` carried in the raw config survive the
  // round-trip unedited.
  const cfg = normalizeConfig('influencer', config);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        className="banner banner-info"
        role="note"
        style={{
          padding: '10px 12px',
          borderRadius: 'var(--r-md)',
          border: '1px solid var(--line)',
          background: 'var(--cream-2)',
          fontSize: 12,
          color: 'var(--ink-2)',
          lineHeight: 1.5,
        }}
      >
        تفاصيل سير عمل حملات المؤثرين ستُضاف لاحقاً. حالياً يمكنك تحديد نطاق الخدمة بنصّ مختصر هنا،
        أمّا التسعير فيُدار من قسم الإعدادات المتقدّمة بالأسفل.
      </div>

      <SimpleField
        label="نطاق ومتطلبات الخدمة"
        value={cfg.details}
        textarea
        maxLength={500}
        placeholder="مثال: ٣ مؤثرين متوسطين في الرياض، فئة طعام، فيديو واحد لكل مؤثر…"
        onChange={(v) => onChange(serializeConfig('influencer', { ...cfg, details: v }))}
      />
    </div>
  );
}
